import { useEffect } from 'react'
import { resolveMediaUrl } from '@/components/Media'
import type { SeoContent, ThemeContent } from '@/lib/content.types'

/** 콘텐츠의 theme/seo 값을 document에 반영한다. */
export function ThemeVars({ theme, seo }: { theme: ThemeContent; seo: SeoContent }) {
  useEffect(() => {
    document.documentElement.style.setProperty('--accent', theme.accent)
  }, [theme.accent])

  useEffect(() => {
    document.title = seo.title
    const set = (selector: string, attr: string, value: string) => {
      const el = document.head.querySelector(selector)
      if (el) el.setAttribute(attr, value)
    }
    set('meta[name="description"]', 'content', seo.description)
    set('meta[property="og:title"]', 'content', seo.title)
    set('meta[property="og:description"]', 'content', seo.description)
    /**
     * og:image 는 **절대 주소**여야 한다 — 공유 크롤러는 우리 페이지를 기준으로
     * 상대 경로를 풀어주지 않는다.
     */
    const ogImage = seo.ogImage?.src
      ? new URL(resolveMediaUrl(seo.ogImage.src), window.location.origin).href
      : ''
    set('meta[property="og:image"]', 'content', ogImage)
    set('meta[property="og:url"]', 'content', window.location.origin + '/')
  }, [seo.title, seo.description, seo.ogImage?.src])

  /**
   * 파비콘(브라우저 탭 아이콘).
   *
   * `index.html` 에 기본 아이콘이 박혀 있으므로, Admin에서 지정했을 때만 그 링크를 바꾼다.
   * 비워두면 손대지 않아 기본 아이콘이 그대로 남는다.
   *
   * 크롬은 같은 주소의 파비콘을 오래 캐시한다. 파일을 새로 올리면 경로가 바뀌므로
   * (`/uploads/...-해시.png`) 캐시가 문제되지 않는다.
   */
  const faviconSrc = seo.favicon?.src ?? ''
  useEffect(() => {
    if (!faviconSrc) return
    const href = resolveMediaUrl(faviconSrc)
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    // 확장자에 맞는 type 을 주지 않으면 일부 브라우저가 무시한다
    const ext = faviconSrc.split('?')[0].split('.').pop()?.toLowerCase()
    const type =
      ext === 'svg' ? 'image/svg+xml' : ext === 'ico' ? 'image/x-icon' : `image/${ext ?? 'png'}`
    link.type = type
    link.href = href
  }, [faviconSrc])

  return null
}
